/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useState, useEffect } from 'react';
import { BrandColors, LinkButton } from '@blend/enterprise-components';
import { css, StyleSheet } from 'aphrodite';

import { accordionMaxHeight } from 'blb-styles/constants';

const styles = StyleSheet.create({
  accordionContainer: {
    alignItems: 'baseline',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    maxHeight: `${accordionMaxHeight}px`,
    overflowX: 'scroll',
  },
  actionBtnLabel: {
    color: BrandColors.NIGHT_60,
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: '20px',
  },
  contentContainer: {
    paddingRight: '5px',
  },
});

export namespace Accordion {
  export interface Props {
    content: string;
    separator?: string;
    truncateAfterLength: number;
  }
}

/**
 * Function returns a label containing the number of words that differ between content and title.
 * @param content
 * @param title
 * @param separator
 */
const getExpandBtnLabel = (content: string, title: string, separator: string) => {
  const contentWordCount = content.split(separator).length;
  const titleWordCount = title.split(separator).length;
  return `${contentWordCount - titleWordCount} others`;
};

export const Accordion: React.FC<Accordion.Props> = ({ content, truncateAfterLength, separator = ',' }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [title, setTitle] = useState<string>(content.substr(0, truncateAfterLength));

  const collapseBtnLabel = 'Collapse';

  useEffect(() => {
    // re-trim if we are in the middle of a word using separator
    setTitle(title.substr(0, Math.min(title.length, title.lastIndexOf(separator))));
  }, []);

  return (
    <div className={css(styles.accordionContainer, styles.actionBtnLabel)}>
      <div className={css(styles.contentContainer)}>{isOpen ? content : `${title}${separator}`}</div>
      <LinkButton
        icon="add"
        inlineFlex={isOpen ? false : true}
        label={isOpen ? collapseBtnLabel : getExpandBtnLabel(content, title, separator)}
        onClick={() => setIsOpen(!isOpen)}
      />
    </div>
  );
};
